import { GraphNavDirection } from '../Cmarkdowngraphcontrols';
import { findMindmapNodeById } from './layout-mindmap';
import type { MindmapTreeNode } from './mindmap-types';

export const MINDMAP_ZOOM_STEP_DURATION_MS = 180;
export const MINDMAP_ZOOM_SLIDER_DURATION_MS = 120;

export enum MindmapKeyAction {
  ArrowRight = 'ArrowRight',
  ArrowLeft = 'ArrowLeft',
  ArrowDown = 'ArrowDown',
  ArrowUp = 'ArrowUp',
  Enter = 'Enter',
  Space = ' ',
  Escape = 'Escape',
  Plus = '+',
  Equals = '=',
  Minus = '-',
  Underscore = '_',
}

export enum MindmapKeyboardActionKind {
  None = 'none',
  ZoomIn = 'zoomIn',
  ZoomOut = 'zoomOut',
  ClearSelection = 'clearSelection',
  Directional = 'directional',
  ToggleActiveNode = 'toggleActiveNode',
}

export enum MindmapNavigationResultKind {
  None = 'none',
  SelectNode = 'selectNode',
  SetExpansion = 'setExpansion',
}

export type MindmapDirectionalKey =
  | MindmapKeyAction.ArrowRight
  | MindmapKeyAction.ArrowLeft
  | MindmapKeyAction.ArrowDown
  | MindmapKeyAction.ArrowUp;

export interface SelectMindmapNodeInput {
  nodeId: string | null;
  fit?: boolean;
}

export interface MindmapExpansionInput {
  nodeId: string;
  forceExpanded?: boolean;
}

export type MindmapKeyboardAction =
  | { kind: MindmapKeyboardActionKind.None }
  | { kind: MindmapKeyboardActionKind.ZoomIn }
  | { kind: MindmapKeyboardActionKind.ZoomOut }
  | { kind: MindmapKeyboardActionKind.ClearSelection }
  | { kind: MindmapKeyboardActionKind.Directional; key: MindmapDirectionalKey }
  | { kind: MindmapKeyboardActionKind.ToggleActiveNode };

export type MindmapNavigationResult =
  | { kind: MindmapNavigationResultKind.None }
  | { kind: MindmapNavigationResultKind.SelectNode; nodeId: string | null; fit?: boolean }
  | { kind: MindmapNavigationResultKind.SetExpansion; nodeId: string; forceExpanded?: boolean };

interface MindmapNavigationSnapshot {
  root: MindmapTreeNode;
  selectedNodeId: string | null;
  expandedNodeIds: Set<string>;
  parentMap: Map<string, string>;
  visibleNodeIds: Set<string>;
  visibleNodeYById: Map<string, number>;
}

interface ResolvedDirectionalNavigationInput extends MindmapNavigationSnapshot {
  activeNode: MindmapTreeNode;
}

type DirectionalNavigationResolver = (
  input: ResolvedDirectionalNavigationInput,
) => MindmapNavigationResult;

const EMPTY_KEYBOARD_ACTION: MindmapKeyboardAction = {
  kind: MindmapKeyboardActionKind.None,
};

const EMPTY_NAVIGATION_RESULT: MindmapNavigationResult = {
  kind: MindmapNavigationResultKind.None,
};

const DIRECTIONAL_KEY_SET: ReadonlySet<string> = new Set<MindmapDirectionalKey>([
  MindmapKeyAction.ArrowRight,
  MindmapKeyAction.ArrowLeft,
  MindmapKeyAction.ArrowDown,
  MindmapKeyAction.ArrowUp,
]);

const KEYBOARD_ACTION_BY_KEY: Record<string, MindmapKeyboardAction> = {
  [MindmapKeyAction.Plus]: { kind: MindmapKeyboardActionKind.ZoomIn },
  [MindmapKeyAction.Equals]: { kind: MindmapKeyboardActionKind.ZoomIn },
  [MindmapKeyAction.Minus]: { kind: MindmapKeyboardActionKind.ZoomOut },
  [MindmapKeyAction.Underscore]: { kind: MindmapKeyboardActionKind.ZoomOut },
  [MindmapKeyAction.Escape]: { kind: MindmapKeyboardActionKind.ClearSelection },
  [MindmapKeyAction.Enter]: { kind: MindmapKeyboardActionKind.ToggleActiveNode },
  [MindmapKeyAction.Space]: { kind: MindmapKeyboardActionKind.ToggleActiveNode },
};

export const GRAPH_NAV_DIRECTION_TO_MINDMAP_KEY: Record<
  GraphNavDirection,
  MindmapDirectionalKey
> = {
  [GraphNavDirection.Up]: MindmapKeyAction.ArrowUp,
  [GraphNavDirection.Down]: MindmapKeyAction.ArrowDown,
  [GraphNavDirection.Left]: MindmapKeyAction.ArrowLeft,
  [GraphNavDirection.Right]: MindmapKeyAction.ArrowRight,
};

const DIRECTIONAL_NAVIGATION_BY_KEY: Record<
  MindmapDirectionalKey,
  DirectionalNavigationResolver
> = {
  [MindmapKeyAction.ArrowRight]: getRightNavigationResult,
  [MindmapKeyAction.ArrowLeft]: getLeftNavigationResult,
  [MindmapKeyAction.ArrowDown]: (input) => getVerticalNavigationResult({
    ...input,
    offset: 1,
  }),
  [MindmapKeyAction.ArrowUp]: (input) => getVerticalNavigationResult({
    ...input,
    offset: -1,
  }),
};

export function getMindmapKeyboardAction(key: string): MindmapKeyboardAction {
  const directionalKey = getDirectionalKey(key);
  if (directionalKey) {
    return {
      kind: MindmapKeyboardActionKind.Directional,
      key: directionalKey,
    };
  }

  return KEYBOARD_ACTION_BY_KEY[key] ?? EMPTY_KEYBOARD_ACTION;
}

export function getDirectionalMindmapNavigation(input: {
  key: MindmapDirectionalKey;
} & MindmapNavigationSnapshot): MindmapNavigationResult {
  const activeNode = findActiveMindmapNode({
    root: input.root,
    selectedNodeId: input.selectedNodeId,
  });

  if (!activeNode) {
    return selectNodeResult(input.root.id);
  }

  return DIRECTIONAL_NAVIGATION_BY_KEY[input.key]({
    ...input,
    activeNode,
  });
}

export function getToggleActiveNodeNavigation(input: {
  root: MindmapTreeNode;
  selectedNodeId: string | null;
}): MindmapNavigationResult {
  const activeNode = findActiveMindmapNode(input);

  if (!activeNode) {
    return selectNodeResult(input.root.id);
  }

  if (activeNode.children.length === 0) {
    return EMPTY_NAVIGATION_RESULT;
  }

  return setExpansionResult({
    nodeId: activeNode.id,
  });
}

function getDirectionalKey(key: string): MindmapDirectionalKey | null {
  return DIRECTIONAL_KEY_SET.has(key) ? (key as MindmapDirectionalKey) : null;
}

function findActiveMindmapNode(input: {
  root: MindmapTreeNode;
  selectedNodeId: string | null;
}): MindmapTreeNode | null {
  return findMindmapNodeById({
    root: input.root,
    nodeId: input.selectedNodeId ?? input.root.id,
  });
}

function getRightNavigationResult(
  input: ResolvedDirectionalNavigationInput,
): MindmapNavigationResult {
  const expansionResult = getRightExpansionResult(input);
  return expansionResult ?? getFirstChildNavigationResult(input.activeNode);
}

function getLeftNavigationResult(
  input: ResolvedDirectionalNavigationInput,
): MindmapNavigationResult {
  if (
    input.activeNode.children.length > 0
    && input.expandedNodeIds.has(input.activeNode.id)
  ) {
    return setExpansionResult({
      nodeId: input.activeNode.id,
      forceExpanded: false,
    });
  }

  const parentNodeId = input.parentMap.get(input.activeNode.id);
  return parentNodeId ? selectNodeResult(parentNodeId) : EMPTY_NAVIGATION_RESULT;
}

function getVerticalNavigationResult(
  input: ResolvedDirectionalNavigationInput & { offset: 1 | -1 },
): MindmapNavigationResult {
  const parentNode = findParentNode(input);
  if (!parentNode) {
    return EMPTY_NAVIGATION_RESULT;
  }

  const targetSiblingNodeId = getOffsetVisibleSiblingNodeId({
    activeNodeId: input.activeNode.id,
    offset: input.offset,
    orderedVisibleSiblingIds: getOrderedVisibleSiblingIds({
      parentNode,
      visibleNodeIds: input.visibleNodeIds,
      visibleNodeYById: input.visibleNodeYById,
    }),
  });
  return targetSiblingNodeId
    ? selectNodeResult(targetSiblingNodeId)
    : EMPTY_NAVIGATION_RESULT;
}

function getRightExpansionResult(
  input: ResolvedDirectionalNavigationInput,
): MindmapNavigationResult | null {
  if (input.activeNode.children.length === 0) {
    return EMPTY_NAVIGATION_RESULT;
  }

  if (input.expandedNodeIds.has(input.activeNode.id)) {
    return null;
  }

  return setExpansionResult({
    nodeId: input.activeNode.id,
    forceExpanded: true,
  });
}

function getFirstChildNavigationResult(activeNode: MindmapTreeNode): MindmapNavigationResult {
  const firstChildId = activeNode.children[0]?.id;
  return firstChildId ? selectNodeResult(firstChildId) : EMPTY_NAVIGATION_RESULT;
}

function findParentNode(input: ResolvedDirectionalNavigationInput): MindmapTreeNode | null {
  const parentNodeId = input.parentMap.get(input.activeNode.id);
  if (!parentNodeId) {
    return null;
  }

  return findMindmapNodeById({
    root: input.root,
    nodeId: parentNodeId,
  });
}

function getOrderedVisibleSiblingIds(input: {
  parentNode: MindmapTreeNode;
  visibleNodeIds: Set<string>;
  visibleNodeYById: Map<string, number>;
}): string[] {
  return input.parentNode.children
    .map((child) => child.id)
    .filter((childId) => input.visibleNodeIds.has(childId))
    .sort((leftId, rightId) => {
      const leftY = input.visibleNodeYById.get(leftId) ?? 0;
      const rightY = input.visibleNodeYById.get(rightId) ?? 0;
      return leftY - rightY;
    });
}

function getOffsetVisibleSiblingNodeId(input: {
  activeNodeId: string;
  offset: 1 | -1;
  orderedVisibleSiblingIds: string[];
}): string | null {
  const currentIndex = input.orderedVisibleSiblingIds.findIndex((childId) => {
    return childId === input.activeNodeId;
  });
  if (currentIndex === -1) {
    return null;
  }

  return input.orderedVisibleSiblingIds[currentIndex + input.offset] ?? null;
}

function selectNodeResult(nodeId: string | null): MindmapNavigationResult {
  return {
    kind: MindmapNavigationResultKind.SelectNode,
    nodeId,
  };
}

function setExpansionResult(input: MindmapExpansionInput): MindmapNavigationResult {
  return {
    kind: MindmapNavigationResultKind.SetExpansion,
    nodeId: input.nodeId,
    forceExpanded: input.forceExpanded,
  };
}
